import React, { Fragment, useCallback, useEffect, useReducer } from "react";
import { useDebouncedCallback } from "use-debounce";
import { useHistory } from "react-router-dom";
import StickyHeaderDataTable from "../shared/sticky-header-data-table";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment-timezone";
import Tooltip from "@mui/material/Tooltip";
import JSONPretty from "react-json-pretty";

import ai from "../../helpers/axios";
import PageHeaderRowSearch from "../shared/page-header-row-search";
import Loader from "../loader";
import currencyFormatter from "../../helpers/currencyFormatter";

const columns = [
  {
    name: "Post Date",
    selector: "transactionDate",
    sortable: true,
    width: "100px",
    cell: (row) => {
      return row.transactionDate ? moment(row.transactionDate).format("M/D/YY") : "N/A";
    },
  },
  {
    name: "Transaction Name",
    selector: "transactionName",
    sortable: true,
    minWidth: "500px",
    cell: (row) => {
      return (
        <Tooltip title={row.transactionName || ""}>
          <div className="two-line-content">
            {row.transactionName || "N/A"}
          </div>
        </Tooltip>
      );
    },
  },
  {
    name: "Pending",
    selector: "pending",
    sortable: true,
    width: "100px",
    cell: (row) => {
      return row.pending ? "Yes" : "No";
    },
  },
  {
    name: "Amount",
    selector: "amount",
    sortable: true,
    cell: (row) => {
      return currencyFormatter.format(row.amount || 0);
    },
    width: "150px",
  },
];

const reducer = (state, action) => {
  switch (action.type) {
    case "dataBeganDownloading": {
      return {
        ...state,
        isLoading: true,
        selectedRowsIDs: [],
      };
    }

    case "dataDownloaded": {
      return {
        ...state,
        isLoading: false,
        data: action?.payload?.data?.data || [],
        totalLength: action?.payload?.data?.countTotal,
      };
    }

    case "pageChanged": {
      return {
        ...state,
        currentPage: action.payload.page,
      };
    }

    case "rowsPerPageChanged": {
      return {
        ...state,
        countPerPage: action.payload.currentRowsPerPage,
        currentPage: 1,
      };
    }

    case "tableSort": {
      return {
        ...state,
        sortBy: action.payload.sortBy,
        sortDirection: action.payload.sortDirection,
        currentPage: 1,
      };
    }

    case "selectRows": {
      return {
        ...state,
        selectedRowsIDs: action.selectedRowsIDs,
        data: [...state.data],
      };
    }

    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    default:
      break;
  }
  return state;
};

const initialState = {
  isLoading: true,
  data: [],
  totalLength: 0,
  countPerPage: 50,
  currentPage: 1,
  sortBy: "transactionDate",
  sortDirection: "desc",
  selectedRowsIDs: [],
};

const PendingPaymentsTransactionDetails = ({ data }) => {
  return (
    <div className="p-3">
      <JSONPretty data={data.item}/>
    </div>
  );
};

const PendingPaymentsContent = () => {
  const history = useHistory();
  const { user } = useAuth0();

  const isAccountingPending = user && user["https://new.evojets.com/appMetadata"]?.isAccountingPending;

  useEffect(() => {
    if (!isAccountingPending) {
      toast.error("You do not have permission to view this page.");
      history.push("/");
    }
  }, [isAccountingPending, history]);

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    document.title = "Pending & Recent Client Payments | evoJets HQ";
  }, []);

  const getData = useCallback(async (params) => {
    dispatch({ type: "dataBeganDownloading" });

    try {
      const token = await getAccessTokenSilently().catch(() => {
        loginWithRedirect();
      });

      const { data } = await ai.auth(token).get(`/api/plaid/pending-transactions`, {
        params,
      });

      dispatch({
        type: "dataDownloaded",
        payload: {
          data,
        },
      });
    } finally {
      dispatch({ type: "fieldChanged", field: "isLoading", value: false });
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const handlePageChange = useCallback((page, totalRows) => {
    dispatch({
      type: "pageChanged",
      payload: {
        page,
        totalRows,
      },
    });
  }, []);

  const handleRowsPerPageChange = useCallback((currentRowsPerPage, currentPage) => {
    dispatch({
      type: "rowsPerPageChanged",
      payload: {
        currentRowsPerPage,
        currentPage,
      },
    });
  }, []);

  const handleSort = useCallback((column, _) => {
    const newDirection =
      column.selector === state.sortBy
        ? state.sortDirection === "asc"
          ? "desc"
          : "asc"
        : "desc";

    dispatch({
      type: "tableSort",
      payload: {
        sortBy: column.selector,
        sortDirection: newDirection,
      },
    });
  }, [state.sortBy, state.sortDirection]);

  const handleRowClick = useCallback((row, event) => {
    event.preventDefault();

    dispatch({
      type: "selectRows",
      selectedRowsIDs: state.selectedRowsIDs.find((selectedRowID) => selectedRowID === row._id) ? [...state.selectedRowsIDs].filter((selectedRowID) => selectedRowID !== row._id) : [
        ...state.selectedRowsIDs,
        row._id,
      ],
    });
  }, [state.selectedRowsIDs]);

  const isSelectableRowSelected = useCallback(
    (row) => state.selectedRowsIDs.find((selectedRowID) => selectedRowID === row._id),
    [state.selectedRowsIDs],
  );

  const debouncedGetData = useDebouncedCallback(
    (params) => {
      getData(params);
    },
    500,
  );

  useEffect(() => {
    debouncedGetData({
      page: state.currentPage,
      countPerPage: state.countPerPage,
      sortBy: state.sortBy,
      sortDirection: state.sortDirection,
    });
  }, [
    debouncedGetData,
    state.currentPage,
    state.countPerPage,
    state.sortBy,
    state.sortDirection,
  ]);

  return (
    <Fragment>
      <PageHeaderRowSearch
        h1="Pending & Recent Client Payments"
        pretitle="Business"
        breadcrumbs={[{ link: "/pending-payments", label: "Pending & Recent Client Payments" }]}
        extraClasses="page-header-trips-l1"
      />

      <div className="server-pagination-table-container trips-list-table-container">
        <StickyHeaderDataTable
          columns={columns}
          data={state.data}
          pagination
          paginationServer
          paginationTotalRows={state.totalLength}
          paginationPerPage={state.countPerPage}
          paginationDefaultPage={state.currentPage}
          paginationRowsPerPageOptions={[10, 25, 50, 100, 150, 200, 250, 500]}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          sortServer
          onSort={handleSort}
          defaultSortFieldId={state.sortBy}
          sortDirection={state.sortDirection}
          progressPending={state.isLoading}
          progressComponent={<Loader/>}
          persistTableHead
          highlightOnHover
          responsive
          onRowClicked={handleRowClick}
          selectableRowSelected={isSelectableRowSelected}
          selectableRowsHighlight
          expandableRows
          expandableRowsComponent={<PendingPaymentsTransactionDetails/>}
        />
      </div>
    </Fragment>
  );
};

export default PendingPaymentsContent;
